<template>
  <PulseModal :open="open" position="center" @close="emit('close')">
    <template #header>Impersonate a user</template>

    <div class="flex flex-col gap-4 p-5">
      <PulseInput v-model="query" placeholder="Search by name or email" data-testid="impersonation-search" />

      <PulseLoadingSpinner v-if="impersonation.loading" />

      <PulseEmptyState
        v-else-if="!impersonation.targets.length"
        title="No eligible users"
        subtitle="No user matches that search, or the matches are not eligible for impersonation."
      />

      <ul v-else class="max-h-96 divide-y divide-neutral-300 overflow-auto" data-testid="impersonation-targets">
        <li v-for="target in impersonation.targets" :key="target.id">
          <button
            type="button"
            class="flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left hover:bg-neutral-100 disabled:opacity-50"
            :disabled="impersonation.starting"
            @click="impersonation.start(target.id)"
          >
            <span class="text-sm font-semibold text-typography-primary">{{ target.displayName }}</span>
            <span class="text-xsm text-typography-secondary">{{ target.email }}</span>
            <span class="text-2xs text-typography-secondary">{{ target.accountName }} · {{ target.roleName }}</span>
          </button>
        </li>
      </ul>

      <p class="text-2xs text-typography-secondary">
        You will see exactly what this user sees. All actions are blocked while impersonating, and the session is
        recorded against your account.
      </p>
    </div>
  </PulseModal>
</template>

<script setup lang="ts">
import { useImpersonationStore } from '~/store/impersonation';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const impersonation = useImpersonationStore();
const query = ref('');

let debounce: ReturnType<typeof setTimeout> | undefined;

// Refresh the (unfiltered) list each time the picker opens, so a target that became ineligible since the last
// open is not still offered.
watch(
  () => props.open,
  isOpen => {
    if (!isOpen) return;
    query.value = '';
    impersonation.loadTargets();
  },
);

watch(query, value => {
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(() => impersonation.loadTargets(value || undefined), 300);
});

onUnmounted(() => {
  if (debounce) clearTimeout(debounce);
});
</script>
